import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import {
  createEmployeeGroupChat,
  deleteEmployeeMessage,
  forwardEmployeeMessage,
  getEmployeeChatOverview,
  markEmployeeChatRead,
  searchEmployeeChatDirectory,
  sendEmployeeChatMessage,
  startEmployeeDirectChat,
  toggleEmployeeChatBlock,
  toggleEmployeeChatMute,
  toggleEmployeeMessageReaction,
  updateEmployeeGroupMembers,
  leaveEmployeeGroupChat,
} from "../api/instateamChatApi";
import { AppIcon, StatusBanner } from "../components/InstateamEmployeeComponents";
import { CHAT_REACTION_OPTIONS, downloadFileFromUrl, formatChatFileSize, formatChatListTimestamp, getChatInitials, isImageAttachment } from "../utils/employeeChatHelpers";
import { connectEmployeeChatSocket } from "../utils/employeeSocketClient";
import {
  getInitials,
  getPresenceStatusMeta,
  resolveEmployeeProfileStatus,
  toAbsoluteUrl,
} from "../utils/employeeHelpers";

const CHAT_TABS = [
  { key: "chats", label: "Chats" },
  { key: "groups", label: "Groups" },
];

const INITIAL_GROUP_FORM = {
  name: "",
  description: "",
  selectedMembers: [],
  searchText: "",
};

function Avatar({
  label,
  avatarUrl,
  fallback,
  className = "",
  profileStatus = "",
  showPresence = false,
}) {
  const presenceMeta = showPresence
    ? getPresenceStatusMeta(resolveEmployeeProfileStatus({ profileStatus }))
    : null;

  const avatarNode = avatarUrl ? (
    <img src={toAbsoluteUrl(avatarUrl)} alt={label} className={className} />
  ) : (
    <div className={`${className} employee-chat-avatar-fallback`}>
      {fallback || getInitials(label)}
    </div>
  );

  if (!showPresence) {
    return avatarNode;
  }

  return (
    <div className="employee-presence-avatar-wrap compact">
      {avatarNode}
      <span
        className={`employee-presence-badge compact ${presenceMeta.className}`}
        title={presenceMeta.label}
        aria-label={presenceMeta.label}
      >
        {presenceMeta.icon ? <AppIcon name={presenceMeta.icon} /> : <span className="employee-presence-dot" />}
      </span>
    </div>
  );
}

function ConversationListItem({ conversation, isActive, onSelect }) {
  return (
    <button
      type="button"
      className={`employee-chat-list-item ${isActive ? "active" : ""}`}
      onClick={() => onSelect(conversation._id)}
    >
      <Avatar
        label={conversation.title}
        avatarUrl={conversation.avatarUrl}
        fallback={conversation.avatarFallback || getChatInitials(conversation.title)}
        className="employee-chat-list-avatar"
        profileStatus={conversation.otherParticipant?.profileStatus}
        showPresence={conversation.type === "direct" && conversation.otherParticipant?.type === "employee"}
      />

      <div className="employee-chat-list-copy">
        <div className="employee-chat-list-row">
          <strong>{conversation.title}</strong>
          <span>{formatChatListTimestamp(conversation.lastMessageAt)}</span>
        </div>
        <p>{conversation.lastMessagePreview || "No messages yet."}</p>
      </div>

      {conversation.unreadCount ? (
        <span className="employee-chat-unread-badge">{conversation.unreadCount}</span>
      ) : null}
    </button>
  );
}

function DirectorySuggestionItem({ entry, onSelect }) {
  return (
    <button
      type="button"
      className="employee-chat-suggestion-item"
      onClick={() => onSelect(entry)}
    >
      <Avatar
        label={entry.fullName}
        avatarUrl={entry.avatarUrl}
        fallback={getInitials(entry.fullName)}
        className="employee-chat-suggestion-avatar"
        profileStatus={entry.profileStatus}
        showPresence={entry.type === "employee"}
      />
      <div>
        <strong>{entry.fullName}</strong>
        <span>{entry.subtitle || entry.email}</span>
      </div>
    </button>
  );
}

function MessageAttachment({ attachment }) {
  if (isImageAttachment(attachment)) {
    return (
      <div className="employee-chat-image-attachment">
        <img src={toAbsoluteUrl(attachment.url)} alt={attachment.fileName} />
        <button
          type="button"
          className="employee-chat-attachment-action"
          onClick={() => downloadFileFromUrl(toAbsoluteUrl(attachment.url), attachment.fileName)}
        >
          <AppIcon name="download" />
        </button>
      </div>
    );
  }

  return (
    <div className="employee-chat-file-attachment">
      <div className="employee-chat-file-icon">
        <AppIcon name="documents" />
      </div>
      <div className="employee-chat-file-copy">
        <strong>{attachment.fileName}</strong>
        <span>{formatChatFileSize(attachment.size)}</span>
      </div>
      <button
        type="button"
        className="employee-chat-attachment-action"
        onClick={() => downloadFileFromUrl(toAbsoluteUrl(attachment.url), attachment.fileName)}
      >
        <AppIcon name="download" />
      </button>
    </div>
  );
}

function MessageBubble({
  message,
  onContextMenu,
  onReactionClick,
}) {
  return (
    <div
      className={`employee-chat-message-row ${message.isOutgoing ? "outgoing" : "incoming"} ${
        message.isSystem ? "system" : ""
      }`}
    >
      {!message.isOutgoing && !message.isSystem ? (
        <Avatar
          label={message.senderName}
          avatarUrl={message.senderAvatarUrl}
          fallback={getInitials(message.senderName)}
          className="employee-chat-message-avatar"
          profileStatus={message.senderProfileStatus}
          showPresence={message.senderType === "employee"}
        />
      ) : null}

      <div
        className={`employee-chat-message-bubble ${message.isOutgoing ? "outgoing" : "incoming"} ${
          message.isSystem ? "system" : ""
        }`}
        onContextMenu={(event) => onContextMenu(event, message)}
      >
        {message.isForwarded ? (
          <div className="employee-chat-forwarded-label">Forwarded</div>
        ) : null}

        {message.showSenderName ? (
          <div className="employee-chat-sender-name">{message.senderName}</div>
        ) : null}

        {message.replyTo ? (
          <div className="employee-chat-reply-preview">
            <strong>{message.replyTo.senderName}</strong>
            <span>{message.replyTo.text || `${message.replyTo.attachmentsCount} attachment(s)`}</span>
          </div>
        ) : null}

        {message.text ? <p>{message.text}</p> : null}

        {message.linkUrls?.length ? (
          <div className="employee-chat-link-list">
            {message.linkUrls.map((linkUrl) => (
              <a key={linkUrl} href={linkUrl} target="_blank" rel="noreferrer">
                {linkUrl}
              </a>
            ))}
          </div>
        ) : null}

        {message.attachments?.length ? (
          <div className="employee-chat-attachment-list">
            {message.attachments.map((attachment) => (
              <MessageAttachment
                key={`${message._id}-${attachment.url}`}
                attachment={attachment}
              />
            ))}
          </div>
        ) : null}

        {!message.isSystem ? (
          <div className="employee-chat-message-meta">
            <span>{message.isPending ? "Sending..." : message.timeLabel}</span>
            {message.isOutgoing ? (
              <span className="employee-chat-read-indicator">
                <AppIcon name={message.readReceipt?.isReadByOthers ? "checkCheck" : "check"} />
              </span>
            ) : null}
          </div>
        ) : null}

        {message.reactions?.length ? (
          <div className="employee-chat-reaction-strip">
            {message.reactions.map((reaction) => (
              <button
                key={`${message._id}-${reaction.emoji}`}
                type="button"
                className={`employee-chat-reaction-pill ${
                  reaction.reactedByCurrentUser ? "active" : ""
                }`}
                title={reaction.participantNames?.length ? reaction.participantNames.join(", ") : reaction.emoji}
                onClick={() => onReactionClick(message, reaction.emoji)}
              >
                <span>{reaction.emoji}</span>
                <strong>{reaction.count}</strong>
                {reaction.participantNames?.length ? (
                  <span className="employee-chat-reaction-tooltip">
                    {reaction.participantNames.join(", ")}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function MessageContextMenu({
  menu,
  onClose,
  onReact,
  onReply,
  onForward,
  onCopy,
  onDeleteMe,
  onDeleteEveryone,
  onDownload,
}) {
  if (!menu) return null;

  return (
    <div
      className="employee-chat-context-menu"
      style={{ top: menu.y, left: menu.x }}
    >
      <div className="employee-chat-context-reactions">
        {CHAT_REACTION_OPTIONS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => {
              onReact(menu.message, emoji);
              onClose();
            }}
          >
            {emoji}
          </button>
        ))}
      </div>
      <button type="button" onClick={() => { onReply(menu.message); onClose(); }}>
        <AppIcon name="reply" />
        Reply
      </button>
      <button type="button" onClick={() => { onForward(menu.message); onClose(); }}>
        <AppIcon name="forward" />
        Forward
      </button>
      <button type="button" onClick={() => { onCopy(menu.message); onClose(); }}>
        <AppIcon name="copy" />
        Copy
      </button>
      {menu.message.attachments?.length ? (
        <button type="button" onClick={() => { onDownload(menu.message); onClose(); }}>
          <AppIcon name="download" />
          Download
        </button>
      ) : null}
      <button type="button" onClick={() => { onDeleteMe(menu.message); onClose(); }}>
        <AppIcon name="trash" />
        Delete for me
      </button>
      {menu.message.canDeleteForEveryone ? (
        <button
          type="button"
          className="danger"
          onClick={() => { onDeleteEveryone(menu.message); onClose(); }}
        >
          <AppIcon name="trash" />
          Delete for everyone
        </button>
      ) : null}
    </div>
  );
}

function GroupModal({
  form,
  directoryResults,
  onClose,
  onChange,
  onToggleMember,
  onSubmit,
  isSubmitting,
  title,
  submitLabel,
}) {
  return (
    <div className="employee-modal-overlay" role="dialog" aria-modal="true">
      <div className="employee-regularization-modal employee-chat-modal">
        <div className="employee-attendance-card-header">
          <div>
            <h3>{title}</h3>
            <p>Select members by searching employees or admins.</p>
          </div>
          <button type="button" className="employee-portal-icon-button" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <div className="employee-regularization-form">
          {"name" in form ? (
            <>
              <label className="form-field">
                <span>Group Name</span>
                <div className="field-shell">
                  <input
                    value={form.name}
                    onChange={(event) => onChange("name", event.target.value)}
                    placeholder="Enter group name"
                  />
                </div>
              </label>

              <label className="form-field">
                <span>Description</span>
                <div className="employee-textarea-shell">
                  <textarea
                    value={form.description}
                    onChange={(event) => onChange("description", event.target.value)}
                    placeholder="Write a short group description"
                  />
                </div>
              </label>
            </>
          ) : null}

          <label className="form-field">
            <span>Search Members</span>
            <div className="field-shell">
              <AppIcon name="search" />
              <input
                value={form.searchText}
                onChange={(event) => onChange("searchText", event.target.value)}
                placeholder="Search employees or admin..."
              />
            </div>
          </label>

          <div className="employee-chat-member-chip-list">
            {form.selectedMembers.map((member) => (
              <button
                key={member.identityKey}
                type="button"
                className="employee-chat-member-chip"
                onClick={() => onToggleMember(member)}
              >
                <span>{member.fullName}</span>
                <AppIcon name="close" />
              </button>
            ))}
          </div>

          <div className="employee-chat-member-search-results">
            {directoryResults.map((entry) => {
              const isSelected = form.selectedMembers.some(
                (member) => member.identityKey === entry.identityKey
              );

              return (
                <button
                  key={entry.identityKey}
                  type="button"
                  className={`employee-chat-member-result ${isSelected ? "selected" : ""}`}
                  onClick={() => onToggleMember(entry)}
                >
                  <Avatar
                    label={entry.fullName}
                    avatarUrl={entry.avatarUrl}
                    fallback={getInitials(entry.fullName)}
                    className="employee-chat-suggestion-avatar"
                    profileStatus={entry.profileStatus}
                    showPresence={entry.type === "employee"}
                  />
                  <div>
                    <strong>{entry.fullName}</strong>
                    <span>{entry.subtitle || entry.email}</span>
                  </div>
                  <AppIcon name={isSelected ? "check" : "userPlus"} />
                </button>
              );
            })}
          </div>

          <div className="employee-regularization-actions">
            <button type="button" className="secondary-button" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="primary-button" onClick={onSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ForwardModal({
  conversations,
  searchText,
  onSearchChange,
  onClose,
  onSelect,
}) {
  return (
    <div className="employee-modal-overlay" role="dialog" aria-modal="true">
      <div className="employee-regularization-modal employee-chat-modal small">
        <div className="employee-attendance-card-header">
          <div>
            <h3>Forward Message</h3>
            <p>Select a chat or group to forward this message.</p>
          </div>
          <button type="button" className="employee-portal-icon-button" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <label className="form-field">
          <span>Search Conversations</span>
          <div className="field-shell">
            <AppIcon name="search" />
            <input
              value={searchText}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Search chat or group name"
            />
          </div>
        </label>

        <div className="employee-chat-forward-list">
          {conversations.map((conversation) => (
            <button
              key={conversation._id}
              type="button"
              className="employee-chat-forward-item"
              onClick={() => onSelect(conversation)}
            >
              <Avatar
                label={conversation.title}
                avatarUrl={conversation.avatarUrl}
                fallback={conversation.avatarFallback || getChatInitials(conversation.title)}
                className="employee-chat-list-avatar"
                profileStatus={conversation.otherParticipant?.profileStatus}
                showPresence={conversation.type === "direct" && conversation.otherParticipant?.type === "employee"}
              />
              <div>
                <strong>{conversation.title}</strong>
                <span>{conversation.subtitle}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ChatDetailModal({ title, onClose, children }) {
  return (
    <div
      className="employee-modal-overlay"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="employee-regularization-modal employee-chat-detail-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="employee-chat-detail-modal-head">
          <div>
            <h3>{title}</h3>
            <p>View the full list inside this conversation.</p>
          </div>
          <button
            type="button"
            className="employee-portal-icon-button"
            onClick={onClose}
          >
            <AppIcon name="close" />
          </button>
        </div>

        <div className="employee-chat-detail-modal-body">{children}</div>
      </div>
    </div>
  );
}

function CompactSidePanelCard({ title, summary, meta = "", onOpen }) {
  return (
    <button type="button" className="employee-chat-side-trigger" onClick={onOpen}>
      <div className="employee-chat-side-trigger-copy">
        <h3>{title}</h3>
        <p>{summary}</p>
        {meta ? <small>{meta}</small> : null}
      </div>
      <span className="employee-chat-side-trigger-arrow">
        <AppIcon name="chevronRight" />
      </span>
    </button>
  );
}

export default function EmployeeChatPage({
  token,
  searchText,
  onSearchChange,
}) {
  const [activeTab, setActiveTab] = useState("chats");
  const [messageSearch, setMessageSearch] = useState("");
  const [selectedConversationId, setSelectedConversationId] = useState("");
  const [overview, setOverview] = useState(null);
  const [directoryResults, setDirectoryResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [composerText, setComposerText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [replyTarget, setReplyTarget] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [groupForm, setGroupForm] = useState(INITIAL_GROUP_FORM);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [manageMembersForm, setManageMembersForm] = useState({
    selectedMembers: [],
    searchText: "",
  });
  const [showManageMembersModal, setShowManageMembersModal] = useState(false);
  const [memberDirectoryResults, setMemberDirectoryResults] = useState([]);
  const [forwardState, setForwardState] = useState({
    open: false,
    message: null,
    searchText: "",
  });
  const [detailModal, setDetailModal] = useState("");
  const [refreshNonce, setRefreshNonce] = useState(0);

  const threadRef = useRef(null);
  const messageSearchRef = useRef(null);
  const fileInputRef = useRef(null);
  const refreshTimerRef = useRef(null);

  const selectedConversation = overview?.selectedConversation || null;
  const allConversations = useMemo(
    () => overview?.allConversations || [],
    [overview?.allConversations]
  );
  const activeConversationId = selectedConversationId || selectedConversation?._id || "";
  const deferredSearchText = useDeferredValue(searchText);
  const deferredMessageSearch = useDeferredValue(messageSearch);
  const deferredGroupSearchText = useDeferredValue(groupForm.searchText);
  const deferredManageMembersSearchText = useDeferredValue(manageMembersForm.searchText);
  function queueRefresh(delay = 120, nextConversationId = "") {
    if (nextConversationId && nextConversationId !== selectedConversationId) {
      setSelectedConversationId(nextConversationId);
    }

    window.clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = window.setTimeout(() => {
      setRefreshNonce((value) => value + 1);
    }, delay);
  }

  function extractLinksFromText(text) {
    return (String(text || "").match(/https?:\/\/[^\s]+/gi) || []).map((item) => item.trim());
  }

  function buildPreviewText(textValue, files) {
    const trimmedText = String(textValue || "").trim();
    if (trimmedText) return trimmedText;
    if (!files.length) return "Sent a message";

    const imageCount = files.filter((file) =>
      String(file.type || "").toLowerCase().startsWith("image/")
    ).length;

    if (imageCount === files.length) {
      return imageCount === 1 ? "Shared an image" : `Shared ${imageCount} images`;
    }

    return files.length === 1 ? "Shared a document" : "Shared files";
  }

  function removeLocalMessage(tempMessageId) {
    setOverview((current) => {
      if (!current?.selectedConversation) return current;

      return {
        ...current,
        selectedConversation: {
          ...current.selectedConversation,
          messages: (current.selectedConversation.messages || []).filter(
            (message) => message._id !== tempMessageId
          ),
        },
      };
    });
  }

  function appendLocalMessage(textValue, files, replyMessage) {
    const now = new Date();
    const tempMessageId = `temp-${now.getTime()}-${Math.random().toString(36).slice(2, 8)}`;
    const formattedTime = new Intl.DateTimeFormat("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(now);
    const attachments = files.map((file) => ({
      fileName: file.name,
      originalName: file.name,
      mimeType: file.type,
      size: Number(file.size || 0),
      url: URL.createObjectURL(file),
      kind: String(file.type || "").toLowerCase().startsWith("image/")
        ? "image"
        : "document",
    }));
    const linkUrls = extractLinksFromText(textValue);
    const previewText = buildPreviewText(textValue, files);
    const tempMessage = {
      _id: tempMessageId,
      senderKey: overview?.currentActor?.identityKey || "",
      senderType: overview?.currentActor?.type || "employee",
      senderName: overview?.currentActor?.fullName || "You",
      senderSubtitle: overview?.currentActor?.subtitle || "",
      senderAvatarUrl: overview?.currentActor?.avatarUrl || "",
      text: String(textValue || "").trim(),
      attachments,
      linkUrls,
      replyTo: replyMessage
        ? {
            _id: replyMessage._id,
            senderName: replyMessage.senderName,
            text: replyMessage.text,
            attachmentsCount: replyMessage.attachments?.length || 0,
            isDeleted: false,
          }
        : null,
      reactions: [],
      isForwarded: false,
      isDeletedForEveryone: false,
      isOutgoing: true,
      isSystem: false,
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
      timeLabel: formattedTime,
      readReceipt: {
        deliveredCount: 0,
        readCount: 0,
        isReadByOthers: false,
      },
      canDeleteForMe: true,
      canDeleteForEveryone: false,
      canReact: true,
      canReply: true,
      showSenderName: false,
      isPending: true,
    };

    setOverview((current) => {
      if (!current?.selectedConversation) return current;

      const updateSummaryList = (items) =>
        (items || []).map((conversation) =>
          conversation._id === activeConversationId
            ? {
                ...conversation,
                lastMessagePreview: previewText,
                lastMessageAt: now.toISOString(),
              }
            : conversation
        );

      const nextSharedFiles = attachments.length
        ? [
            ...attachments.map((attachment) => ({
              url: attachment.url,
              fileName: attachment.fileName,
              kind: attachment.kind,
              size: attachment.size,
              senderName: current.currentActor?.fullName || "You",
              createdAt: now.toISOString(),
            })),
            ...(current.selectedConversation.sharedFiles || []),
          ].slice(0, 12)
        : current.selectedConversation.sharedFiles || [];

      const nextSharedLinks = linkUrls.length
        ? [
            ...linkUrls.map((linkUrl) => ({
              url: linkUrl,
              senderName: current.currentActor?.fullName || "You",
              createdAt: now.toISOString(),
            })),
            ...(current.selectedConversation.sharedLinks || []),
          ].slice(0, 12)
        : current.selectedConversation.sharedLinks || [];

      return {
        ...current,
        allConversations: updateSummaryList(current.allConversations),
        conversations: updateSummaryList(current.conversations),
        selectedConversation: {
          ...current.selectedConversation,
          messages: [...(current.selectedConversation.messages || []), tempMessage],
          sharedFiles: nextSharedFiles,
          sharedLinks: nextSharedLinks,
        },
      };
    });

    return tempMessageId;
  }

  function applyLocalReactionUpdate(messageId, emoji) {
    setOverview((current) => {
      if (!current?.selectedConversation) return current;

      const actorName = current.currentActor?.fullName || "You";
      const nextMessages = (current.selectedConversation.messages || []).map((message) => {
        if (message._id !== messageId) return message;

        const baseReactions = (message.reactions || [])
          .map((reaction) => {
            const filteredNames = (reaction.participantNames || []).filter(
              (participantName) => participantName !== actorName
            );
            const nextCount = reaction.reactedByCurrentUser
              ? Math.max(0, Number(reaction.count || 0) - 1)
              : Number(reaction.count || 0);

            return {
              ...reaction,
              count: nextCount,
              reactedByCurrentUser: false,
              participantNames: filteredNames,
            };
          })
          .filter((reaction) => reaction.count > 0);

        const existingReaction = (message.reactions || []).find(
          (reaction) => reaction.emoji === emoji && reaction.reactedByCurrentUser
        );

        if (!existingReaction) {
          const matchedReaction = baseReactions.find((reaction) => reaction.emoji === emoji);
          if (matchedReaction) {
            matchedReaction.count += 1;
            matchedReaction.reactedByCurrentUser = true;
            if (!matchedReaction.participantNames.includes(actorName)) {
              matchedReaction.participantNames = [...matchedReaction.participantNames, actorName];
            }
          } else {
            baseReactions.push({
              emoji,
              count: 1,
              reactedByCurrentUser: true,
              participantNames: [actorName],
            });
          }
        }

        return {
          ...message,
          reactions: baseReactions,
        };
      });

      return {
        ...current,
        selectedConversation: {
          ...current.selectedConversation,
          messages: nextMessages,
        },
      };
    });
  }

  useEffect(() => {
    let isMounted = true;

    async function fetchOverview() {
      try {
        setIsLoading(true);
        const payload = await getEmployeeChatOverview(token, {
          tab: activeTab,
          search: deferredSearchText,
          messageSearch: deferredMessageSearch,
          conversationId: selectedConversationId,
        });

        if (!isMounted) return;
        setOverview(payload.overview || null);
      } catch (error) {
        if (!isMounted) return;
        setStatusMessage({
          type: "error",
          text: error.message,
        });
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    fetchOverview();

    return () => {
      isMounted = false;
    };
  }, [
    activeTab,
    deferredMessageSearch,
    deferredSearchText,
    refreshNonce,
    selectedConversationId,
    token,
  ]);

  useEffect(() => {
    if (!selectedConversation?._id || !selectedConversation.unreadCount) return;

    markEmployeeChatRead(token, selectedConversation._id).catch(() => {});
  }, [selectedConversation?._id, selectedConversation?.unreadCount, token]);

  useEffect(() => {
    if (!deferredSearchText || deferredSearchText.trim().length < 2) {
      setDirectoryResults([]);
      return;
    }

    let isMounted = true;
    searchEmployeeChatDirectory(token, { search: deferredSearchText })
      .then((payload) => {
        if (isMounted) {
          setDirectoryResults(payload.directory || []);
        }
      })
      .catch(() => {
        if (isMounted) {
          setDirectoryResults([]);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [deferredSearchText, token]);

  useEffect(() => {
    if (!showGroupModal && !showManageMembersModal) {
      setMemberDirectoryResults([]);
      return;
    }

    const currentSearch = showGroupModal
      ? deferredGroupSearchText
      : deferredManageMembersSearchText;
    if (!currentSearch || currentSearch.trim().length < 2) {
      setMemberDirectoryResults([]);
      return;
    }

    let isMounted = true;
    searchEmployeeChatDirectory(token, {
      search: currentSearch,
      conversationId: showManageMembersModal ? selectedConversation?._id : "",
    })
      .then((payload) => {
        if (isMounted) {
          setMemberDirectoryResults(payload.directory || []);
        }
      })
      .catch(() => {
        if (isMounted) {
          setMemberDirectoryResults([]);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [
    deferredGroupSearchText,
    deferredManageMembersSearchText,
    selectedConversation?._id,
    showGroupModal,
    showManageMembersModal,
    token,
  ]);

  useEffect(() => {
    let isMounted = true;
    let socketInstance = null;

    connectEmployeeChatSocket(token)
      .then((socket) => {
        if (!isMounted) {
          socket.disconnect();
          return;
        }

        socketInstance = socket;
        socket.on("instateam:conversation:refresh", () => {
          window.clearTimeout(refreshTimerRef.current);
          refreshTimerRef.current = window.setTimeout(() => {
            setRefreshNonce((value) => value + 1);
          }, 120);
        });
      })
      .catch((error) => {
        if (isMounted) {
          setStatusMessage({
            type: "warning",
            text: error.message,
          });
        }
      });

    return () => {
      isMounted = false;
      if (socketInstance) {
        socketInstance.disconnect();
      }
    };
  }, [token]);

  useEffect(() => {
    return () => {
      window.clearTimeout(refreshTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [selectedConversation?._id, selectedConversation?.messages?.length]);

  useEffect(() => {
    setDetailModal("");
  }, [selectedConversation?._id]);

  useEffect(() => {
    const closeMenu = () => setContextMenu(null);
    window.addEventListener("click", closeMenu);
    return () => window.removeEventListener("click", closeMenu);
  }, []);

  const filteredForwardTargets = useMemo(() => {
    const searchLower = forwardState.searchText.trim().toLowerCase();

    return allConversations.filter((conversation) => {
      if (conversation._id === selectedConversation?._id) return false;
      if (!searchLower) return true;

      return [conversation.title, conversation.subtitle]
        .join(" ")
        .toLowerCase()
        .includes(searchLower);
    });
  }, [allConversations, forwardState.searchText, selectedConversation?._id]);

  async function refreshView(nextConversationId = activeConversationId) {
    queueRefresh(120, nextConversationId);
  }

  async function handleOpenDirectChat(entry) {
    try {
      setIsSubmitting(true);
      const payload = await startEmployeeDirectChat(token, {
        targetType: entry.type,
        targetId: entry._id,
      });
      onSearchChange("");
      setDirectoryResults([]);
      setActiveTab("chats");
      queueRefresh(20, payload.conversationId || "");
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleChatWithAdmin() {
    const adminContact = (overview?.adminContacts || [])[0];
    if (!adminContact) {
      setStatusMessage({
        type: "warning",
        text: "No admin contact is available right now.",
      });
      return;
    }

    handleOpenDirectChat(adminContact);
  }

  async function handleSendMessage(event) {
    event.preventDefault();
    if (!selectedConversation?._id) return;

    const messageText = composerText;
    const filesToSend = [...selectedFiles];
    const replyMessage = replyTarget;
    const formData = new FormData();
    formData.append("text", messageText);
    if (replyMessage?._id) {
      formData.append("replyToMessageId", replyMessage._id);
    }
    filesToSend.forEach((file) => formData.append("files", file));

    const tempMessageId = appendLocalMessage(messageText, filesToSend, replyMessage);
    setComposerText("");
    setSelectedFiles([]);
    setReplyTarget(null);

    try {
      setIsSubmitting(true);
      await sendEmployeeChatMessage(token, selectedConversation._id, formData);
      queueRefresh(80, selectedConversation._id);
    } catch (error) {
      removeLocalMessage(tempMessageId);
      setComposerText(messageText);
      setSelectedFiles(filesToSend);
      setReplyTarget(replyMessage);
      setStatusMessage({
        type: "error",
        text: error.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleToggleReaction(message, emoji) {
    try {
      applyLocalReactionUpdate(message._id, emoji);
      await toggleEmployeeMessageReaction(token, message._id, emoji);
      queueRefresh(100, selectedConversation?._id);
    } catch (error) {
      queueRefresh(0, selectedConversation?._id);
      setStatusMessage({
        type: "error",
        text: error.message,
      });
    }
  }

  async function handleDeleteMessage(message, scope) {
    try {
      await deleteEmployeeMessage(token, message._id, scope);
      await refreshView(selectedConversation?._id);
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
    }
  }

  async function handleForwardMessage(conversation) {
    if (!forwardState.message) return;

    try {
      setIsSubmitting(true);
      await forwardEmployeeMessage(token, forwardState.message._id, conversation._id);
      setForwardState({
        open: false,
        message: null,
        searchText: "",
      });
      await refreshView(selectedConversation?._id);
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleToggleMute() {
    if (!selectedConversation?._id) return;
    try {
      await toggleEmployeeChatMute(
        token,
        selectedConversation._id,
        !selectedConversation.isMuted
      );
      await refreshView(selectedConversation._id);
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
    }
  }

  async function handleToggleBlock() {
    if (!selectedConversation?._id) return;
    const shouldBlock = !selectedConversation.blockedByCurrentUser;
    try {
      await toggleEmployeeChatBlock(token, selectedConversation._id, shouldBlock);
      await refreshView(selectedConversation._id);
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
    }
  }

  async function handleLeaveGroup() {
    if (!selectedConversation?._id) return;
    if (!window.confirm("Do you want to leave this group?")) return;

    try {
      await leaveEmployeeGroupChat(token, selectedConversation._id);
      setSelectedConversationId("");
      await refreshView("");
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
    }
  }

  async function handleCreateGroup() {
    if (!groupForm.name.trim()) {
      setStatusMessage({
        type: "warning",
        text: "Please enter a group name.",
      });
      return;
    }

    if (!groupForm.selectedMembers.length) {
      setStatusMessage({
        type: "warning",
        text: "Please select at least one member.",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = await createEmployeeGroupChat(token, {
        name: groupForm.name,
        description: groupForm.description,
        members: groupForm.selectedMembers.map((member) => ({
          type: member.type,
          id: member._id,
        })),
      });
      setShowGroupModal(false);
      setGroupForm(INITIAL_GROUP_FORM);
      setActiveTab("groups");
      setSelectedConversationId(payload.conversationId || "");
      await refreshView(payload.conversationId || "");
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleAddMembers() {
    if (!selectedConversation?._id || !manageMembersForm.selectedMembers.length) {
      return;
    }

    try {
      setIsSubmitting(true);
      await updateEmployeeGroupMembers(token, selectedConversation._id, {
        action: "add",
        members: manageMembersForm.selectedMembers.map((member) => ({
          type: member.type,
          id: member._id,
        })),
      });
      setShowManageMembersModal(false);
      setManageMembersForm({
        selectedMembers: [],
        searchText: "",
      });
      await refreshView(selectedConversation._id);
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleRemoveMember(member) {
    if (!selectedConversation?._id) return;
    if (!window.confirm(`Remove ${member.fullName} from this group?`)) return;

    try {
      await updateEmployeeGroupMembers(token, selectedConversation._id, {
        action: "remove",
        members: [{ type: member.type, id: member._id }],
      });
      await refreshView(selectedConversation._id);
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
    }
  }

  function handleCopyMessage(message) {
    navigator.clipboard
      .writeText(message.text || message.linkUrls?.[0] || "")
      .then(() => {
        setStatusMessage({
          type: "success",
          text: "Message copied to clipboard.",
        });
      })
      .catch(() => {
        setStatusMessage({
          type: "warning",
          text: "Unable to copy this message.",
        });
      });
  }

  function handleDownloadMessage(message) {
    (message.attachments || []).forEach((attachment) => {
      downloadFileFromUrl(toAbsoluteUrl(attachment.url), attachment.fileName);
    });
  }

  function toggleSelectedGroupMember(entry) {
    setGroupForm((current) => {
      const exists = current.selectedMembers.some(
        (member) => member.identityKey === entry.identityKey
      );

      return {
        ...current,
        selectedMembers: exists
          ? current.selectedMembers.filter(
              (member) => member.identityKey !== entry.identityKey
            )
          : [...current.selectedMembers, entry],
      };
    });
  }

  function toggleManagedMember(entry) {
    setManageMembersForm((current) => {
      const exists = current.selectedMembers.some(
        (member) => member.identityKey === entry.identityKey
      );

      return {
        ...current,
        selectedMembers: exists
          ? current.selectedMembers.filter(
              (member) => member.identityKey !== entry.identityKey
            )
          : [...current.selectedMembers, entry],
      };
    });
  }

  return (
    <section className="employee-portal-page employee-chat-page">
      <div className="employee-page-heading employee-chat-heading">
        <div>
          <h1>Chat</h1>
          <div className="employee-breadcrumb">
            <span>Dashboard</span>
            <span>›</span>
            <span>Chat</span>
          </div>
        </div>
      </div>

      {statusMessage ? (
        <StatusBanner
          type={statusMessage.type}
          text={statusMessage.text}
          onClose={() => setStatusMessage(null)}
        />
      ) : null}

      <div className="employee-chat-layout">
        <aside className="employee-chat-sidebar-card">
          <div className="employee-chat-sidebar-topbar">
            <label className="employee-portal-search employee-chat-inner-search">
              <AppIcon name="search" />
              <input
                value={searchText}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder="Search messages or people..."
              />
            </label>

            <button type="button" className="employee-portal-icon-button" onClick={() => setShowGroupModal(true)}>
              <AppIcon name="edit" />
            </button>
          </div>

          {directoryResults.length ? (
            <div className="employee-chat-suggestion-list">
              {directoryResults.map((entry) => (
                <DirectorySuggestionItem
                  key={entry.identityKey}
                  entry={entry}
                  onSelect={handleOpenDirectChat}
                />
              ))}
            </div>
          ) : null}

          <div className="employee-chat-tab-row">
            {CHAT_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`employee-chat-tab ${activeTab === tab.key ? "active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="employee-chat-list">
            {(overview?.conversations || []).map((conversation) => (
              <ConversationListItem
                key={conversation._id}
                conversation={conversation}
                isActive={activeConversationId === conversation._id}
                onSelect={setSelectedConversationId}
              />
            ))}

            {!isLoading && !(overview?.conversations || []).length ? (
              <div className="employee-chat-empty-list">
                <h3>No conversations yet</h3>
                <p>Start a chat by searching an employee, group, or admin.</p>
              </div>
            ) : null}
          </div>

          <button
            type="button"
            className="secondary-button employee-chat-admin-button"
            onClick={handleChatWithAdmin}
          >
            <AppIcon name="chat" />
            Chat with Admin
          </button>
        </aside>

        <div className="employee-chat-main-card">
          {selectedConversation ? (
            <>
              <div className="employee-chat-conversation-header">
                <div className="employee-chat-conversation-title">
                  <Avatar
                    label={selectedConversation.title}
                    avatarUrl={selectedConversation.avatarUrl}
                    fallback={selectedConversation.avatarFallback || getChatInitials(selectedConversation.title)}
                    className="employee-chat-conversation-avatar"
                    profileStatus={selectedConversation.otherParticipant?.profileStatus}
                    showPresence={
                      selectedConversation.type === "direct" &&
                      selectedConversation.otherParticipant?.type === "employee"
                    }
                  />
                  <div>
                    <strong>{selectedConversation.title}</strong>
                    <span>
                      {selectedConversation.type === "group"
                        ? `${selectedConversation.memberCount} members`
                        : selectedConversation.otherParticipant?.subtitle || "Direct chat"}
                    </span>
                  </div>
                </div>

                <div className="employee-chat-conversation-actions">
                  <button
                    type="button"
                    className="employee-portal-icon-button"
                    onClick={() => messageSearchRef.current?.focus()}
                  >
                    <AppIcon name="search" />
                  </button>
                  {/* <button type="button" className="employee-portal-icon-button" disabled>
                    <AppIcon name="phone" />
                  </button>
                  <button type="button" className="employee-portal-icon-button" disabled>
                    <AppIcon name="video" />
                  </button> */}
                  <button type="button" className="employee-portal-icon-button" disabled>
                    <AppIcon name="infoCircle" />
                  </button>
                </div>
              </div>

              <div className="employee-chat-content-grid">
                <div className="employee-chat-thread-column">
                  <div className="employee-chat-thread" role="log" ref={threadRef}>
                    {selectedConversation.messages.map((message) => (
                      <MessageBubble
                        key={message._id}
                        message={message}
                        onContextMenu={(event, targetMessage) => {
                          event.preventDefault();
                          setContextMenu({
                            x: event.clientX,
                            y: event.clientY,
                            message: targetMessage,
                          });
                        }}
                        onReactionClick={handleToggleReaction}
                      />
                    ))}
                  </div>

                  {replyTarget ? (
                    <div className="employee-chat-composer-note">
                      <div>
                        <strong>Replying to {replyTarget.senderName}</strong>
                        <span>{replyTarget.text || "Attachment message"}</span>
                      </div>
                      <button type="button" onClick={() => setReplyTarget(null)}>
                        <AppIcon name="close" />
                      </button>
                    </div>
                  ) : null}

                  {selectedFiles.length ? (
                    <div className="employee-chat-selected-files">
                      {selectedFiles.map((file, index) => (
                        <div key={`${file.name}-${index}`} className="employee-chat-selected-file">
                          <span>{file.name}</span>
                          <button
                            type="button"
                            onClick={() =>
                              setSelectedFiles((current) =>
                                current.filter((_, fileIndex) => fileIndex !== index)
                              )
                            }
                          >
                            <AppIcon name="close" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  <form className="employee-chat-composer" onSubmit={handleSendMessage}>
                    <button
                      type="button"
                      className="employee-portal-icon-button"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <AppIcon name="paperclip" />
                    </button>
                    <input
                      value={composerText}
                      onChange={(event) => setComposerText(event.target.value)}
                      placeholder={
                        selectedConversation.canSend
                          ? "Type a message..."
                          : selectedConversation.blockedByOtherParticipant
                            ? "This chat is blocked by the other participant."
                            : selectedConversation.blockedByCurrentUser
                              ? "You blocked this chat."
                              : "You cannot send messages in this group."
                      }
                      disabled={!selectedConversation.canSend || isSubmitting}
                    />
                    <button type="button" className="employee-portal-icon-button" disabled>
                      <AppIcon name="smile" />
                    </button>
                    <button
                      type="submit"
                      className="primary-button employee-chat-send-button"
                      disabled={
                        isSubmitting ||
                        !selectedConversation.canSend ||
                        (!composerText.trim() && !selectedFiles.length)
                      }
                    >
                      <AppIcon name="send" />
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                      multiple
                      hidden
                      onChange={(event) =>
                        setSelectedFiles((current) => [
                          ...current,
                          ...(Array.from(event.target.files || [])),
                        ])
                      }
                    />
                  </form>
                </div>

                <aside className="employee-chat-side-panel">
                  <label className="employee-portal-search employee-chat-inner-search compact">
                    <AppIcon name="search" />
                    <input
                      ref={messageSearchRef}
                      value={messageSearch}
                      onChange={(event) => setMessageSearch(event.target.value)}
                      placeholder="Search in chat..."
                    />
                  </label>

                  <div className="employee-chat-side-card employee-chat-side-card-compact">
                    <CompactSidePanelCard
                      title={
                        selectedConversation.type === "group"
                          ? `Members (${selectedConversation.memberCount})`
                          : "Members"
                      }
                      summary={
                        selectedConversation.type === "group"
                          ? `${selectedConversation.memberCount || 0} members in this group`
                          : `${(selectedConversation.members || []).length} people in this conversation`
                      }
                      meta="Click to view the full member list"
                      onOpen={() => setDetailModal("members")}
                    />
                    {selectedConversation.canManageMembers ? (
                      <button
                        type="button"
                        className="link-button"
                        onClick={() => setShowManageMembersModal(true)}
                      >
                        <AppIcon name="userPlus" />
                        Add Members
                      </button>
                    ) : null}
                  </div>

                  <div className="employee-chat-side-card employee-chat-side-card-compact">
                    <CompactSidePanelCard
                      title={selectedConversation.type === "group" ? "About Group" : "Chat Details"}
                      summary={
                        selectedConversation.description ||
                        (selectedConversation.type === "group"
                          ? "Open to view group description and conversation info."
                          : selectedConversation.otherParticipant?.subtitle ||
                            "Open to view the full conversation details.")
                      }
                      meta={
                        selectedConversation.type === "group"
                          ? `Created by ${selectedConversation.createdByName} • ${selectedConversation.memberCount} members`
                          : "Click to view the full details"
                      }
                      onOpen={() => setDetailModal("details")}
                    />
                  </div>

                  <div className="employee-chat-side-card employee-chat-side-card-compact">
                    <CompactSidePanelCard
                      title="Shared Files"
                      summary={
                        (selectedConversation.sharedFiles || []).length
                          ? `${selectedConversation.sharedFiles.length} file${
                              selectedConversation.sharedFiles.length > 1 ? "s" : ""
                            } shared in this chat`
                          : "No files shared yet"
                      }
                      meta={
                        (selectedConversation.sharedFiles || []).length
                          ? "Click to view and download all shared files"
                          : "Open to view the shared files panel"
                      }
                      onOpen={() => setDetailModal("files")}
                    />
                  </div>

                  <div className="employee-chat-side-card employee-chat-side-card-compact">
                    <CompactSidePanelCard
                      title="Shared Links"
                      summary={
                        (selectedConversation.sharedLinks || []).length
                          ? `${selectedConversation.sharedLinks.length} link${
                              selectedConversation.sharedLinks.length > 1 ? "s" : ""
                            } shared in this chat`
                          : "No links shared yet"
                      }
                      meta={
                        (selectedConversation.sharedLinks || []).length
                          ? "Click to view all shared links"
                          : "Open to view the shared links panel"
                      }
                      onOpen={() => setDetailModal("links")}
                    />
                  </div>

                  <div className="employee-chat-side-card employee-chat-side-card-compact">
                    <CompactSidePanelCard
                      title="Chat Settings"
                      summary={
                        selectedConversation.isMuted
                          ? "Notifications muted for this conversation"
                          : "Manage notifications and chat actions"
                      }
                      meta={
                        selectedConversation.canBlockDirect
                          ? selectedConversation.blockedByCurrentUser
                            ? "Chat is currently blocked"
                            : "Block or unblock this direct chat"
                          : selectedConversation.canLeaveGroup
                            ? "Leave the group from the settings popup"
                            : "Open to manage this conversation"
                      }
                      onOpen={() => setDetailModal("settings")}
                    />
                  </div>
                </aside>
              </div>
            </>
          ) : (
            <div className="employee-coming-soon-card compact">
              <h2>Select a chat</h2>
              <p>Choose an employee, admin, or group from the left to start messaging.</p>
            </div>
          )}
        </div>
      </div>

      <MessageContextMenu
        menu={contextMenu}
        onClose={() => setContextMenu(null)}
        onReact={handleToggleReaction}
        onReply={setReplyTarget}
        onForward={(message) =>
          setForwardState({
            open: true,
            message,
            searchText: "",
          })
        }
        onCopy={handleCopyMessage}
        onDeleteMe={(message) => handleDeleteMessage(message, "me")}
        onDeleteEveryone={(message) => handleDeleteMessage(message, "everyone")}
        onDownload={handleDownloadMessage}
      />

      {showGroupModal ? (
        <GroupModal
          form={groupForm}
          directoryResults={memberDirectoryResults}
          onClose={() => {
            setShowGroupModal(false);
            setGroupForm(INITIAL_GROUP_FORM);
          }}
          onChange={(key, value) =>
            setGroupForm((current) => ({
              ...current,
              [key]: value,
            }))
          }
          onToggleMember={toggleSelectedGroupMember}
          onSubmit={handleCreateGroup}
          isSubmitting={isSubmitting}
          title="Create Group"
          submitLabel="Create Group"
        />
      ) : null}

      {showManageMembersModal ? (
        <GroupModal
          form={manageMembersForm}
          directoryResults={memberDirectoryResults}
          onClose={() => {
            setShowManageMembersModal(false);
            setManageMembersForm({
              selectedMembers: [],
              searchText: "",
            });
          }}
          onChange={(key, value) =>
            setManageMembersForm((current) => ({
              ...current,
              [key]: value,
            }))
          }
          onToggleMember={toggleManagedMember}
          onSubmit={handleAddMembers}
          isSubmitting={isSubmitting}
          title="Add Group Members"
          submitLabel="Add Members"
        />
      ) : null}

      {forwardState.open ? (
        <ForwardModal
          conversations={filteredForwardTargets}
          searchText={forwardState.searchText}
          onSearchChange={(value) =>
            setForwardState((current) => ({
              ...current,
              searchText: value,
            }))
          }
          onClose={() =>
            setForwardState({
              open: false,
              message: null,
              searchText: "",
            })
          }
          onSelect={handleForwardMessage}
        />
      ) : null}

      {detailModal === "details" && selectedConversation ? (
        <ChatDetailModal
          title={selectedConversation.type === "group" ? "About Group" : "Chat Details"}
          onClose={() => setDetailModal("")}
        >
          <div className="employee-chat-detail-copy">
            <div className="employee-chat-detail-copy-card">
              <h4>Description</h4>
              <p>
                {selectedConversation.description ||
                  (selectedConversation.type === "group"
                    ? "No group description has been added yet."
                    : selectedConversation.otherParticipant?.subtitle ||
                      "Direct employee conversation")}
              </p>
            </div>

            <div className="employee-chat-detail-meta-grid">
              <div className="employee-chat-detail-meta-item">
                <span>Conversation Type</span>
                <strong>{selectedConversation.type === "group" ? "Group Chat" : "Direct Chat"}</strong>
              </div>

              <div className="employee-chat-detail-meta-item">
                <span>Total Members</span>
                <strong>{selectedConversation.memberCount || (selectedConversation.members || []).length || 0}</strong>
              </div>

              {selectedConversation.type === "group" ? (
                <div className="employee-chat-detail-meta-item">
                  <span>Created By</span>
                  <strong>{selectedConversation.createdByName || "Unknown"}</strong>
                </div>
              ) : null}

              <div className="employee-chat-detail-meta-item">
                <span>Shared Files</span>
                <strong>{(selectedConversation.sharedFiles || []).length}</strong>
              </div>

              <div className="employee-chat-detail-meta-item">
                <span>Shared Links</span>
                <strong>{(selectedConversation.sharedLinks || []).length}</strong>
              </div>

              <div className="employee-chat-detail-meta-item">
                <span>Notifications</span>
                <strong>{selectedConversation.isMuted ? "Muted" : "Active"}</strong>
              </div>
            </div>
          </div>
        </ChatDetailModal>
      ) : null}

      {detailModal === "members" && selectedConversation ? (
        <ChatDetailModal title="All Members" onClose={() => setDetailModal("")}>
          <div className="employee-chat-detail-member-list">
            {(selectedConversation.members || []).length ? (
              (selectedConversation.members || []).map((member) => (
                <div key={member.identityKey} className="employee-chat-member-item">
                  <Avatar
                    label={member.fullName}
                    avatarUrl={member.avatarUrl}
                    fallback={getInitials(member.fullName)}
                    className="employee-chat-member-avatar"
                    profileStatus={member.profileStatus}
                    showPresence={member.type === "employee"}
                  />
                  <div>
                    <strong>
                      {member.fullName}
                      {member.isCurrent ? " (You)" : ""}
                    </strong>
                    <span>
                      {member.presenceStatus === "left"
                        ? "Left"
                        : member.presenceStatus === "online"
                          ? "Online"
                          : "Offline"}
                    </span>
                  </div>
                  {member.role ? (
                    <small className="employee-chat-member-role">{member.role}</small>
                  ) : null}
                  {member.canRemove ? (
                    <button
                      type="button"
                      className="employee-portal-icon-button"
                      onClick={() => handleRemoveMember(member)}
                    >
                      <AppIcon name="userMinus" />
                    </button>
                  ) : null}
                </div>
              ))
            ) : (
              <div className="employee-chat-detail-empty">No members found.</div>
            )}
          </div>
        </ChatDetailModal>
      ) : null}

      {detailModal === "files" && selectedConversation ? (
        <ChatDetailModal title="All Shared Files" onClose={() => setDetailModal("")}>
          <div className="employee-chat-detail-file-list">
            {(selectedConversation.sharedFiles || []).length ? (
              (selectedConversation.sharedFiles || []).map((file) => (
                <button
                  key={`${file.url}-${file.createdAt}`}
                  type="button"
                  className="employee-chat-shared-item"
                  onClick={() => downloadFileFromUrl(toAbsoluteUrl(file.url), file.fileName)}
                >
                  <AppIcon name="documents" />
                  <div>
                    <strong>{file.fileName}</strong>
                    <span>{formatChatFileSize(file.size)} • {file.senderName}</span>
                  </div>
                </button>
              ))
            ) : (
              <div className="employee-chat-detail-empty">No shared files yet.</div>
            )}
          </div>
        </ChatDetailModal>
      ) : null}

      {detailModal === "links" && selectedConversation ? (
        <ChatDetailModal title="All Shared Links" onClose={() => setDetailModal("")}>
          <div className="employee-chat-detail-link-list">
            {(selectedConversation.sharedLinks || []).length ? (
              (selectedConversation.sharedLinks || []).map((linkItem) => (
                <a key={linkItem.url} href={linkItem.url} target="_blank" rel="noreferrer">
                  <AppIcon name="link" />
                  <span>{linkItem.url}</span>
                </a>
              ))
            ) : (
              <div className="employee-chat-detail-empty">No shared links yet.</div>
            )}
          </div>
        </ChatDetailModal>
      ) : null}

      {detailModal === "settings" && selectedConversation ? (
        <ChatDetailModal title="Chat Settings" onClose={() => setDetailModal("")}>
          <div className="employee-chat-detail-copy">
            <button
              type="button"
              className="employee-chat-setting-row"
              onClick={handleToggleMute}
            >
              <span>Mute Notifications</span>
              <strong>{selectedConversation.isMuted ? "On" : "Off"}</strong>
            </button>

            {selectedConversation.canBlockDirect ? (
              <button
                type="button"
                className="employee-chat-setting-row danger"
                onClick={handleToggleBlock}
              >
                <span>
                  {selectedConversation.blockedByCurrentUser ? "Unblock Chat" : "Block Chat"}
                </span>
                <AppIcon name="ban" />
              </button>
            ) : null}

            {selectedConversation.canLeaveGroup ? (
              <button
                type="button"
                className="employee-chat-setting-row danger"
                onClick={handleLeaveGroup}
              >
                <span>Leave Group</span>
                <AppIcon name="logOut" />
              </button>
            ) : null}
          </div>
        </ChatDetailModal>
      ) : null}
    </section>
  );
}
